import { createContext, useContext, type ReactNode } from 'react';

/**
 * Context for providing portal containers to nested components.
 *
 * This solves a race condition where Radix UI portals (Select, Popover, etc.)
 * need to render inside a dialog container, but the container lookup via
 * useLayoutEffect is async and can fail on slower systems.
 *
 * By providing the container through context, nested components get the
 * container synchronously without DOM queries.
 */

interface PortalContainerContextValue {
  container: HTMLElement | null;
}

const PortalContainerContext = createContext<PortalContainerContextValue>({
  container: null,
});

interface PortalContainerProviderProps {
  children: ReactNode;
  /**
   * Portal container element. Use a callback ref in the caller to keep
   * this value in sync with the DOM node lifecycle.
   */
  container: HTMLElement | null;
}

/**
 * Provides a portal container to all nested Select, Popover, and DatePicker
 * components. Used by Dialog and Drawer to ensure nested portals render
 * inside the dialog rather than document.body.
 */
export function PortalContainerProvider({ children, container }: PortalContainerProviderProps) {
  return (
    <PortalContainerContext.Provider value={{ container }}>
      {children}
    </PortalContainerContext.Provider>
  );
}

/**
 * Hook to get the portal container from context.
 * Returns null if not inside a PortalContainerProvider (e.g., outside dialogs).
 */
export function usePortalContainer(): HTMLElement | null {
  const { container } = useContext(PortalContainerContext);
  return container;
}
